import { decorateReward } from '@/common/reward/linkable';
import { StringUtils } from '@/common/utils/stringUtils';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { LiveopsRewardType } from '@/modules/liveops/item/types';
import type { LiveopsLeaderboardReward } from '@/modules/liveops/leaderboard/types';
import { useLiveopsReward } from '@/modules/liveops/shared/reward';
import { liveopsPointService } from '@/services/liveops';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useLiveopsLeaderboardList() {
    const route = useRoute();
    const { executeAction, confirmAndRemove } = useCrudActions();
    const { fetchLinkableRewards, linkableRewards } = useLiveopsReward();

    const itemInfo = reactive({
        id: StringUtils.toNumber(route.params.itemId),
        rewardType: LiveopsRewardType.Unknown,
        canEditLeaderboard: false
    });

    const loadAndSetLiveopsItem = executeAction({
        canExecute: () => itemInfo.id != null,
        request: () => liveopsPointService.item.get(itemInfo.id),
        onSuccess: ({ data }) => {
            itemInfo.rewardType = data.rewardType;
            itemInfo.canEditLeaderboard = data.canEditLeaderboard;
        }
    });

    const { list, total, fetchList } = useBaseListView({
        request: liveopsPointService.leaderboard.list,
        paramsBuilder: () => ({ itemId: itemInfo.id }),
        hooks: {
            loadInitialData: async () => await Promise.all([fetchLinkableRewards(), loadAndSetLiveopsItem()]),
            transformResponse: (items: LiveopsLeaderboardReward[]) =>
                items.map(item => decorateReward(item, linkableRewards.value))
        }
    });

    const actions = {
        remove: confirmAndRemove({
            request: (leaderboardId: number) => liveopsPointService.leaderboard.remove(itemInfo.id, leaderboardId),
            onSuccess: () => fetchList()
        })
    };

    return { list, total, fetchList, itemInfo, actions };
}
